// Dunne API-client: cookies, CSRF-header, JSON en een vaste foutvorm.
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: Record<string, string[]>;
  readonly details: unknown;
  readonly requestId: string | null;
  constructor(status: number, body: { code?: string; message?: string; fieldErrors?: Record<string, string[]>; details?: unknown; requestId?: string }) {
    super(body.message ?? `HTTP ${status}`);
    this.status = status;
    this.code = body.code ?? "unknown";
    this.fieldErrors = body.fieldErrors ?? {};
    this.details = body.details;
    this.requestId = body.requestId ?? null;
  }
  get isConflict(): boolean {
    return this.status === 409;
  }
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

async function request<T>(method: Method, path: string, body?: unknown, headers: Record<string, string> = {}): Promise<T> {
  const init: RequestInit = { method, credentials: "same-origin", headers: { accept: "application/json", "x-bres-csrf": "1", ...headers } };
  if (body instanceof FormData) init.body = body;
  else if (body !== undefined) {
    init.body = JSON.stringify(body);
    (init.headers as Record<string, string>)["content-type"] = "application/json";
  }
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch {
    throw new ApiError(0, { code: "network", message: "network" });
  }
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new CustomEvent("bres:unauthenticated"));
    throw new ApiError(res.status, data as ConstructorParameters<typeof ApiError>[1]);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) => request<T>("POST", path, body ?? {}, headers),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body ?? {}),
  patch: <T>(path: string, body?: unknown, headers?: Record<string, string>) => request<T>("PATCH", path, body ?? {}, headers),
  delete: <T>(path: string) => request<T>("DELETE", path),
};

/** Querystring zonder lege waarden. */
export function qs(params: Record<string, string | number | boolean | null | undefined | string[]>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) continue;
    if (Array.isArray(v)) v.forEach((x) => q.append(k, x));
    else q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}
